/*
 *  -----------------------------------------------------------------------
 *    <copyright file="site-report.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from '@progress/kendo-data-query';

import { useTranslation } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { validateFields } from 'utils/validation';

import './site-report.scss';
import './styles.scss';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import KRIScoresGrid from 'views/components/central-monitoring/reports/kri-scores-grid';
import PreviewExpandable from 'views/components/central-monitoring/reports/preview-expandable';
import LegendIcons from '@components/central-monitoring/legend-icons/legend-icons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import TextField from 'views/components/core/text-field';

const SiteReport = ({
    site,
    study,
    setSiteReviews,
    dataSetId,
    studyReview,
    onUpdateErrorState,
    siteReviewInfo,
}: {
    site: any;
    study: StudyFields;
    setSiteReviews: Function;
    dataSetId: string;
    studyReview: any;
    onUpdateErrorState: Function;
    siteReviewInfo: any;
}) => {
    const { t } = useTranslation();

    const [observations, setObservations] = useState([]);
    const [reviewSummaryError, setReviewSummaryError] = useState(null);

    useEffect(() => {
        if (site && study) {
            if (site.observations?.length > 0) {
                const array = [...site.observations].map((observation: any) => {
                    const _recentActionDate = observation.actions
                        .map((action: any) => action.requestDate)
                        .sort()
                        .reverse()[0];
                    const actions = observation.actions.map((action: any) => ({
                        ...action,
                        observationId: observation.id,
                        _isCompletedDate: Boolean(action.completedDate),
                    }));

                    return {
                        ...observation,
                        _isClosedDate: Boolean(observation.closedDate),
                        _recentActionDate: _recentActionDate ? new Date(_recentActionDate) : null,
                        actions: orderBy(actions, [
                            { field: '_isClosedDate', dir: 'asc' },
                            { field: 'updated', dir: 'desc' },
                        ]),
                    };
                });
                const newData = restructuredUpdatedCreatedBy(array);
                setObservations(
                    orderBy(newData, [
                        { field: '_isClosedDate', dir: 'asc' },
                        { field: 'updated', dir: 'desc' },
                    ]),
                );
            }
        }
    }, [site, study, siteReviewInfo]);

    const icon =
        site.siteScoreChange?.toLowerCase() === 'upper'
            ? 'arrow-circle-up'
            : site.siteScoreChange?.toLowerCase() === 'lower'
              ? 'arrow-circle-down'
              : 'minus-circle';

    const reviewSummary = siteReviewInfo?.summary ?? site.reviewSummary ?? '' ?? '';

    const onChange = (event: any) => {
        setSiteReviews(site.siteId, {
            summary: event.target.value,
            reviewer: site.reviewer ?? '',
            reviewed: site.reviewed,
            startDate: studyReview?.reviewStartDate || null,
            completedDate: studyReview?.reviewCompleteDate || null,
            siteId: site.siteId,
            dataSetId,
        });

        const errorSummary = validateFields(event.target.value, 'reviewSummary', 1000, false);
        setReviewSummaryError(errorSummary.reviewSummary);

        onUpdateErrorState(site.siteId, errorSummary.reviewSummary);
    };

    return (
        <div style={{ marginBottom: 30 }}>
            <PreviewExpandable
                header={
                    <div className="header-top-part">
                        <p className="site">{t('site_num', { id: site?.site })}</p>
                        <p className="reviewed-site">
                            <span className="title">{t('cm:reviewed')}:</span> {site.reviewed === true ? 'Yes' : 'No'}
                        </p>
                    </div>
                }
                isExpanded={false}
                children={
                    <div className="site-wrapper">
                        <div className="top-site-content">
                            <div className="left-part">
                                <div className="study-summary-wrapper">
                                    {study?.siteSummaryConfiguration.siteActiveDate ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('ra:activeDate')}:</p>{' '}
                                            {formatDateUTC_DEPRECATED(site.siteActiveDate, true, 'DD-MMM-YYYY')}
                                        </p>
                                    ) : null}
                                    {study?.siteSummaryConfiguration.siteCountry ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('country')}:</p> {site.siteCountry}
                                        </p>
                                    ) : null}
                                    {study?.siteSummaryConfiguration.siteStatus ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('ra:status')}:</p> {site.siteStatus}
                                        </p>
                                    ) : null}
                                    {study?.siteSummaryConfiguration.monitor ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('admin:monitor')}:</p> {site.siteMonitor}
                                        </p>
                                    ) : null}
                                    {study?.siteSummaryConfiguration.numEnrolled ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('cm:enrolledSubjects')}:</p> {site.enrolledSubjects}
                                        </p>
                                    ) : null}
                                    {study?.siteSummaryConfiguration.numScreened ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('cm:screenedSubjects')}:</p> {site.screenedSubjects}
                                        </p>
                                    ) : null}
                                    {study?.siteSummaryConfiguration.numScreenFailed ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('cm:screenedFailedSubjects')}:</p>{' '}
                                            {site.screenedFailedSubjects}
                                        </p>
                                    ) : null}
                                    {study?.siteSummaryConfiguration.numRandomised ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('cm:#randomised')}:</p> {site.numRandomised}
                                        </p>
                                    ) : null}
                                    {study?.siteSummaryConfiguration.numTreatmentDiscontinued ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('cm:treatmentDiscontinuedSubjects')}:</p>{' '}
                                            {site.numTreatmentDiscontinued}
                                        </p>
                                    ) : null}
                                    {study?.siteSummaryConfiguration.numStudyDiscontinued ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('cm:discontinuedSubjects')}:</p>{' '}
                                            {site.discontinuedSubjects}
                                        </p>
                                    ) : null}
                                    {study?.siteSummaryConfiguration.numCompleted ? (
                                        <p className="text-wrap">
                                            <p className="title">{t('cm:numCompleted')}:</p> {site.numCompleted}
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                            <div className="right-part-site-report">
                                <div className="score-info">
                                    <FontAwesomeIcon
                                        icon={['far', icon]}
                                        className={`arrow-circle ${site.siteScoreColour?.toLowerCase() || 'black'}`}
                                    />
                                    <p className="score">{site.siteScore > 0 ? site.siteScore.toFixed(2) : '--'}</p>
                                </div>
                                <p className="descr">{t('admin:siteScore')}</p>
                            </div>
                        </div>
                        <div className="divider-blue" />
                        <p className="text-wrap" style={{ marginBottom: 20 }}>
                            <p className="title">{t('reports:reviewer')}:</p> {site.reviewer}
                        </p>
                        <TextField
                            label={t('cm:reviewSummary')}
                            id="review-summary"
                            rows={4}
                            fullWidth
                            multiline
                            value={reviewSummary}
                            onChange={onChange}
                            size="small"
                            placeholder={t('enterText')}
                            variant="outlined"
                            color="secondary"
                            className="grey-color-label"
                            helperText={
                                <CharactersRemaining
                                    id={`review-summary-text-${site?.site}`}
                                    length={reviewSummary.length}
                                    max={1000}
                                    error={reviewSummaryError}
                                />
                            }
                        />
                        <div className="divider-blue" />
                        <LegendIcons showIncludeScoring={false} />
                        <KRIScoresGrid kriScores={site.kriScores} />
                        <div className="divider-blue" />
                        <div className="observations-actions-wrapper">
                            <p className="subtitle">{t('ra:observationsAndActions')}</p>
                            {observations?.length ? (
                                observations.map((item: any) => (
                                    <div>
                                        <div className="flex-wrap-text">
                                            <p className="text-wrap">
                                                <p className="title">ID:</p> {item.recordId || item.id}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:observationDate')}:</p>{' '}
                                                {item.observationDate}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('cm:panelName')}:</p> {item.panelName}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('cm:datasetDate')}:</p> {item.dataSetDate}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:openActions')}:</p> {item.openActions || 0}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:recentAction')}:</p>{' '}
                                                {formatDateUTC_DEPRECATED(item._recentActionDate, true, 'DD-MMM-YYYY')}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('owner')}:</p> {item.owner}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:closedDate')}:</p> {item.closedDate}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:updatedBy')}:</p> {item.updatedBy}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:updatedDate')}:</p> {item.updatedDate}
                                            </p>
                                        </div>
                                        <p className="title-text">{t('ra:observation')}:</p>
                                        <p className="content-text">{item.observation}</p>
                                        <p className="title-text">{t('ra:potentialCause')}:</p>
                                        <p className="content-text">{item.potentialCause}</p>
                                        <p className="title-text">{t('ra:confirmedCause')}:</p>
                                        <p className="content-text">{item.confirmedCause}</p>
                                        <p className="title-text">{t('ra:comments')}:</p>
                                        <p className="content-text">{item.comments}</p>
                                        <p className="subtitle">{t('ra:action(s)')}</p>
                                        {item.actions?.length ? (
                                            item.actions.map((el: any, index: number) => (
                                                <div key={el.id}>
                                                    <div className="flex-wrap-text">
                                                        <p className="text-wrap">
                                                            <p className="title">ID:</p> {el.id}
                                                        </p>
                                                        <p className="text-wrap">
                                                            <p className="title">{t('ra:requestedDate')}:</p>{' '}
                                                            {el.requestDate}
                                                        </p>
                                                        <p className="text-wrap">
                                                            <p className="title">{t('owner')}:</p> {el.owner}
                                                        </p>
                                                        <p className="text-wrap">
                                                            <p className="title">{t('ra:expectedCompletionDate')}:</p>{' '}
                                                            {el.expectedCompleteDate}
                                                        </p>
                                                        <p className="text-wrap">
                                                            <p className="title">{t('ra:completedDate')}:</p>{' '}
                                                            {el.completedDate}
                                                        </p>
                                                        <p className="text-wrap">
                                                            <p className="title">{t('ra:updatedBy')}:</p> {el.updatedBy}
                                                        </p>
                                                        <p className="text-wrap">
                                                            <p className="title">{t('ra:updatedDate')}:</p>{' '}
                                                            {el.updatedDate}
                                                        </p>
                                                    </div>
                                                    <p className="title-text">{t('description')}:</p>
                                                    <p className="content-text">{el.description}</p>
                                                    <p className="title-text">{t('cm:actionTaken')}:</p>
                                                    <p className="content-text">{el.actionTaken}</p>
                                                    <p className="title-text">{t('cm:rootCause')}:</p>
                                                    <p className="content-text">{el.rootCause}</p>
                                                    <p className="title-text">{t('result')}:</p>
                                                    <p className="content-text">{el.result}</p>
                                                    {index < item.actions.length - 1 ? (
                                                        <div className="divider-black" />
                                                    ) : null}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="no-records no-pt">{t('ra:noAssociatedActionRecords')}</p>
                                        )}
                                        <div className="divider-blue" />
                                    </div>
                                ))
                            ) : (
                                <p className="no-records no-pt">{t('ra:noAssociatedObservationRecords')}</p>
                            )}
                        </div>
                    </div>
                }
                customKey="cm-site-report"
            />
        </div>
    );
};

export default SiteReport;
